import { useId } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  NAMESPACE_LABELS,
  type ConfigNamespace,
  type TerminalCashRounding,
  type TerminalCatalogueScope,
  type TerminalConfigValue,
  type TerminalLoyalty,
  type TerminalTaxGroup,
} from '@/hooks/use-terminals';
import { useT } from '@/i18n/use-t';

/**
 * Editors for the terminal-config namespaces (RFL-241, loyalty link YUKI-50).
 *
 * Shared by the profile (group) editor and the per-terminal override editor,
 * so a field cannot exist at one level and not the other — which would show up
 * as a setting an operator can author for a group but never override for the
 * one lane that needs it.
 *
 * Values are edited as the till's own key names. See the type declarations in
 * `use-terminals.ts` for why those names are not translated into this
 * codebase's conventions.
 */

/** The shape `useT` returns, for the helpers below that cannot call a hook. */
type Translate = (key: string, bakedEnglish?: string) => string;

/** Falls back to the baked English, for callers that have no `t` to hand. */
const bakedOnly: Translate = (key, bakedEnglish) => bakedEnglish ?? key;

/**
 * Catalogue key per namespace. `NAMESPACE_LABELS` stays the baked English —
 * the labels are shared with the profile editor, and one registry of keys here
 * keeps the parity guard able to see the whole set.
 */
const NAMESPACE_KEY: Record<ConfigNamespace, string> = {
  cash_rounding: 'terminals.namespace.cashRounding',
  tax_rules: 'terminals.namespace.taxGroups',
  fiscal: 'terminals.namespace.fiscalBinding',
  loyalty: 'terminals.namespace.loyaltyLink',
  catalogue_scope: 'terminals.namespace.catalogueScope',
};

/** The translated name of a config namespace. */
export function namespaceLabel(namespace: ConfigNamespace, translate: Translate = bakedOnly) {
  return translate(NAMESPACE_KEY[namespace], NAMESPACE_LABELS[namespace]);
}

/** Empty starting values, for when a namespace is first switched on. */
export const NAMESPACE_DEFAULTS: {
  [K in ConfigNamespace]: NonNullable<TerminalConfigValue[K]>;
} = {
  cash_rounding: { enable_round_precision: true, round_precision: 50 },
  tax_rules: { tax_groups: [{ code: '' }] },
  fiscal: { bindingId: '' },
  loyalty: { base_url: '' },
  catalogue_scope: { store_catalogue_price_list_id: 0 },
};

export function CashRoundingFields({
  value,
  onChange,
  disabled,
}: {
  value: TerminalCashRounding;
  onChange: (next: TerminalCashRounding) => void;
  disabled?: boolean;
}) {
  const t = useT('admin_ui');
  const roundPrecisionId = useId();
  const roundingErrorId = useId();
  const roundModeId = useId();
  return (
    <div className="grid gap-3 md:grid-cols-2">
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          className="h-4 w-4"
          checked={value.enable_round_precision}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, enable_round_precision: e.target.checked })}
        />
        {t('terminals.config.roundCashTotals', 'Round cash totals')}
      </label>
      <div>
        <Label htmlFor={roundPrecisionId}>{t('terminals.config.step', 'Step (minor units)')}</Label>
        <Input
          id={roundPrecisionId}
          type="number"
          min={1}
          value={String(value.round_precision)}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, round_precision: Number(e.target.value) })}
        />
        {/* Minor units, because a cash-rounding step expressed as a float is a
            rounding rule that stops being reproducible. */}
        <p className="mt-1 text-xs text-muted-foreground">
          {t('terminals.config.stepHelp', '50 = round to the nearest 0.50. Applies to cash only.')}
        </p>
      </div>
      <div>
        <Label htmlFor={roundModeId}>{t('terminals.config.roundMode', 'Rounding direction')}</Label>
        {/* RL-793: absent means down, the behaviour every till had before the
            field existed. Choosing down removes the key rather than writing it,
            so an untouched config is delivered exactly as before. */}
        <select
          id={roundModeId}
          className="h-9 w-full rounded-md border bg-background px-2 text-sm"
          value={value.round_mode ?? 'down'}
          disabled={disabled}
          onChange={(e) => {
            const next: TerminalCashRounding = { ...value, round_mode: 'nearest' };
            if (e.target.value !== 'nearest') delete next.round_mode;
            onChange(next);
          }}
        >
          <option value="down">{t('terminals.config.roundModeDown', 'Down (default)')}</option>
          <option value="nearest">{t('terminals.config.roundModeNearest', 'Nearest')}</option>
        </select>
      </div>
      <div className="md:col-span-2">
        <Label htmlFor={roundingErrorId}>
          {t(
            'terminals.config.roundingErrorMessage',
            'Message shown when rounding fails (optional)',
          )}
        </Label>
        <Input
          id={roundingErrorId}
          value={value.rounding_error_message ?? ''}
          disabled={disabled}
          onChange={(e) =>
            onChange({
              ...value,
              rounding_error_message: e.target.value === '' ? undefined : e.target.value,
            })
          }
        />
      </div>
    </div>
  );
}

export function TaxGroupFields({
  value,
  onChange,
  disabled,
}: {
  value: { tax_groups: TerminalTaxGroup[] };
  onChange: (next: { tax_groups: TerminalTaxGroup[] }) => void;
  disabled?: boolean;
}) {
  const t = useT('admin_ui');
  const set = (index: number, patch: Partial<TerminalTaxGroup>) => {
    const next = value.tax_groups.map((g, i) => (i === index ? { ...g, ...patch } : g));
    onChange({ tax_groups: next });
  };
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 text-xs text-muted-foreground">
        <span>{t('terminals.config.groupCode', 'Group code')}</span>
        <span>{t('terminals.config.vatPercent', 'VAT %')}</span>
        <span>{t('terminals.config.excisePercent', 'Excise %')}</span>
        <span />
      </div>
      {value.tax_groups.map((group, index) => (
        <div key={index} className="grid grid-cols-[1fr_1fr_1fr_auto] items-center gap-2">
          {/* The letter the fiscal device knows (В, Г, …) — not a name we choose. */}
          <Input
            aria-label={t('terminals.config.groupCodeAria', 'Group code {n}').replace(
              '{n}',
              String(index + 1),
            )}
            value={group.code}
            disabled={disabled}
            onChange={(e) => set(index, { code: e.target.value })}
          />
          {/* Rates are decimal STRINGS. A VAT rate that round-trips through a
              float can arrive as 19.999999999999998, and this value is printed
              on a fiscal receipt. */}
          <Input
            aria-label={t('terminals.config.vatRateAria', 'VAT rate {n}').replace(
              '{n}',
              String(index + 1),
            )}
            inputMode="decimal"
            placeholder="20"
            value={group.vat_rate ?? ''}
            disabled={disabled}
            onChange={(e) =>
              set(index, { vat_rate: e.target.value === '' ? undefined : e.target.value })
            }
          />
          <Input
            aria-label={t('terminals.config.exciseRateAria', 'Excise rate {n}').replace(
              '{n}',
              String(index + 1),
            )}
            inputMode="decimal"
            placeholder="—"
            value={group.excise_rate ?? ''}
            disabled={disabled}
            onChange={(e) =>
              set(index, { excise_rate: e.target.value === '' ? undefined : e.target.value })
            }
          />
          <Button
            type="button"
            variant="ghost"
            disabled={disabled || value.tax_groups.length === 1}
            onClick={() => onChange({ tax_groups: value.tax_groups.filter((_, i) => i !== index) })}
          >
            {t('terminals.config.remove', 'Remove')}
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        disabled={disabled}
        onClick={() => onChange({ tax_groups: [...value.tax_groups, { code: '' }] })}
      >
        {t('terminals.config.addGroup', 'Add group')}
      </Button>
    </div>
  );
}

export function FiscalFields({
  value,
  onChange,
  disabled,
}: {
  value: { bindingId: string };
  onChange: (next: { bindingId: string }) => void;
  disabled?: boolean;
}) {
  const t = useT('admin_ui');
  const bindingId = useId();
  return (
    <div>
      <Label htmlFor={bindingId}>{t('terminals.config.bindingId', 'Binding id')}</Label>
      <Input
        id={bindingId}
        value={value.bindingId}
        placeholder="ua-prro"
        disabled={disabled}
        onChange={(e) => onChange({ bindingId: e.target.value })}
      />
      <p className="mt-1 text-xs text-muted-foreground">
        {t(
          'terminals.config.bindingHelpPrefix',
          'A binding the terminal registry already understands, e.g.',
        )}{' '}
        <code>ua-prro</code> {t('terminals.config.or', 'or')} <code>lt-vmi-vf</code>
        {t('terminals.config.bindingHelpSuffix', '. Lowercase letters, digits and dashes.')}
      </p>
    </div>
  );
}

export function LoyaltyFields({
  value,
  onChange,
  disabled,
}: {
  value: TerminalLoyalty;
  onChange: (next: TerminalLoyalty) => void;
  disabled?: boolean;
}) {
  const t = useT('admin_ui');
  const baseUrlId = useId();
  return (
    <div>
      <Label htmlFor={baseUrlId}>{t('terminals.config.loyaltyBaseUrl', 'Loyalty base URL')}</Label>
      <Input
        id={baseUrlId}
        type="url"
        inputMode="url"
        maxLength={255}
        value={value.base_url}
        placeholder="https://loyalty.example.com/api"
        disabled={disabled}
        onChange={(e) => onChange({ base_url: e.target.value })}
      />
      {/* Addressing, not an operator preference: it moves with the deployment,
          and it rides terminal-config so relocating the loyalty host is picked
          up by a sync instead of re-pairing every terminal. The credential is
          deliberately NOT here — it lives in the terminal's OS keystore and is
          entered on the terminal, because this channel is cached in the clear
          in the terminal database. */}
      <p className="mt-1 text-xs text-muted-foreground">
        {t(
          'terminals.config.loyaltyHelp',
          'Absolute URL, including scheme. Leave this namespace unset unless the estate has a direct loyalty link — terminals read an absent value as "no link" rather than as an error. Never enter a credential here.',
        )}
      </p>
    </div>
  );
}

export function CatalogueScopeFields({
  value,
  onChange,
  disabled,
}: {
  value: TerminalCatalogueScope;
  onChange: (next: TerminalCatalogueScope) => void;
  disabled?: boolean;
}) {
  const t = useT('admin_ui');
  const priceListId = useId();
  return (
    <div>
      <Label htmlFor={priceListId}>
        {t('terminals.config.storeCataloguePriceList', 'Store catalogue price list')}
      </Label>
      {/* VL-166: a selector the till applies to the superset it already holds,
          so it can refuse a known barcode as "not sold at this terminal"
          instead of calling it unknown. */}
      <Input
        id={priceListId}
        type="number"
        min={0}
        step={1}
        value={String(value.store_catalogue_price_list_id)}
        disabled={disabled}
        onChange={(e) => onChange({ store_catalogue_price_list_id: Number(e.target.value) })}
      />
      <p className="mt-1 text-xs text-muted-foreground">
        {t(
          'terminals.config.catalogueScopeHelp',
          'The price list number this terminal sells from; 0 is the main list. Barcodes that exist only on other lists are refused as not sold at this terminal. Leave this namespace unset to keep the main list.',
        )}
      </p>
    </div>
  );
}

/**
 * Compile-time proof that every namespace has an editor. Reached only if a
 * `switch` above stopped being exhaustive, at which point `namespace` is no
 * longer `never` and this call fails to typecheck.
 */
function assertNamespaceHandled(namespace: never): never {
  throw new Error(`No editor implemented for config namespace: ${String(namespace)}`);
}

/** Render whichever namespace editor applies, with a common signature. */
export function NamespaceFields({
  namespace,
  value,
  onChange,
  disabled,
}: {
  namespace: ConfigNamespace;
  value: NonNullable<TerminalConfigValue[ConfigNamespace]>;
  onChange: (next: NonNullable<TerminalConfigValue[ConfigNamespace]>) => void;
  disabled?: boolean;
}) {
  switch (namespace) {
    case 'cash_rounding':
      return (
        <CashRoundingFields
          value={value as TerminalCashRounding}
          onChange={onChange}
          disabled={disabled}
        />
      );
    case 'tax_rules':
      return (
        <TaxGroupFields
          value={value as { tax_groups: TerminalTaxGroup[] }}
          onChange={onChange}
          disabled={disabled}
        />
      );
    case 'fiscal':
      return (
        <FiscalFields
          value={value as { bindingId: string }}
          onChange={onChange}
          disabled={disabled}
        />
      );
    case 'loyalty':
      return (
        <LoyaltyFields value={value as TerminalLoyalty} onChange={onChange} disabled={disabled} />
      );
    case 'catalogue_scope':
      return (
        <CatalogueScopeFields
          value={value as TerminalCatalogueScope}
          onChange={onChange}
          disabled={disabled}
        />
      );
    default:
      // A new contract namespace must fail the build HERE too, not only at the
      // label and default registries. Without this, a missing case widens the
      // return type to `| undefined`, which React happily renders as nothing —
      // so the namespace would be listed, toggleable, and silently uneditable.
      return assertNamespaceHandled(namespace);
  }
}

/** One-line summary of a configured namespace, for inheritance display. */
export function summariseNamespace(
  namespace: ConfigNamespace,
  value: NonNullable<TerminalConfigValue[ConfigNamespace]> | undefined,
  translate: Translate = bakedOnly,
): string {
  if (value === undefined) return translate('terminals.config.notSet', 'not set');
  switch (namespace) {
    case 'cash_rounding': {
      const v = value as TerminalCashRounding;
      if (!v.enable_round_precision) return translate('terminals.config.disabled', 'disabled');
      return v.round_mode === 'nearest'
        ? translate(
            'terminals.config.roundToNearest',
            'round to the nearest {step} minor units',
          ).replace('{step}', String(v.round_precision))
        : translate('terminals.config.roundTo', 'round to {step} minor units').replace(
            '{step}',
            String(v.round_precision),
          );
    }
    case 'tax_rules': {
      const v = value as { tax_groups: TerminalTaxGroup[] };
      return translate('terminals.config.groupsSummary', '{count} group(s): {codes}')
        .replace('{count}', String(v.tax_groups.length))
        .replace('{codes}', v.tax_groups.map((g) => g.code).join(', '));
    }
    case 'fiscal':
      return (value as { bindingId: string }).bindingId;
    case 'loyalty':
      return (value as TerminalLoyalty).base_url;
    case 'catalogue_scope':
      return translate('terminals.config.priceListSummary', 'price list {id}').replace(
        '{id}',
        String((value as TerminalCatalogueScope).store_catalogue_price_list_id),
      );
  }
}
